import React, { useState } from "react";
import { useI18n } from "../i18n";

export default function SubscriptionBanner({ user, subStatus, onSubscribe, showCreateButton = true, quotaInfo = null }) {
  const { t } = useI18n();
  const [info, setInfo] = useState("");
  const [pending, setPending] = useState(false);

  const showPaywall = subStatus && subStatus.valid === false;
  const isTrial = subStatus?.plan === 'trial';
  const isPro = !!(subStatus?.valid) && subStatus?.plan !== 'trial';
  
  // Check if quota is exceeded for trial users
  const isQuotaExceeded = quotaInfo && quotaInfo.quotaExceeded;
  const showCreationRestricted = isTrial && showCreateButton && isQuotaExceeded;

  // Don't show banner for active paid users
  if (isPro) {
    return null;
  }

  const handleSubscribe = async () => {
    if (pending) return;
    setPending(true);
    try {
      const msg = await onSubscribe?.();
      if (msg) setInfo(msg);
    } catch (error) {
      setInfo("Failed to subscribe. Please try again.");
    } finally {
      setPending(false);
    }
  };

  // Show paywall banner for inactive users
  if (showPaywall) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-4">
        <div className="text-sm font-semibold text-yellow-800">{t('dash.paywall.title')}</div>
        <div className="text-xs text-yellow-700 mt-1">{t('dash.paywall.desc')}</div>
        {quotaInfo && (
          <div className="text-xs text-yellow-600 mt-1">
            <div>Usage: {quotaInfo.combined?.usage || (quotaInfo.orders.usage + quotaInfo.expenses.usage)} / {quotaInfo.combined?.limit || quotaInfo.orders.limit} total entries</div>
          </div>
        )}
        <button 
          disabled={pending} 
          onClick={handleSubscribe} 
          className={`mt-3 text-white text-sm px-3 py-1.5 rounded ${pending ? 'bg-green-300 cursor-not-allowed' : 'bg-green-600'}`}
        >
          {pending ? t('dash.paywall.pending') : t('dash.paywall.subscribe')}
        </button>
        {(pending || info) && (
          <div className="text-xs text-green-700 mt-2">{info || t('dash.paywall.info')}</div>
        )}
      </div>
    );
  }

  // Show trial banner for trial users
  if (isTrial) {
    return (
      <div className="bg-green-50 border border-green-100 rounded-2xl p-3 mb-4 shadow-sm flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-green-800">{t('dash.trial.title')}</div>
          <div className="text-xs text-gray-700 mt-0.5">{t('dash.trial.subtitle')}</div>
          {quotaInfo && (
            <div className="text-xs text-gray-600 mt-1">
              <div>Usage: {quotaInfo.combined?.usage || (quotaInfo.orders.usage + quotaInfo.expenses.usage)} / {quotaInfo.combined?.limit || quotaInfo.orders.limit} total entries</div>
            </div>
          )}
          {showCreationRestricted && (
            <div className="text-xs text-yellow-700 mt-1 bg-yellow-100 px-2 py-1 rounded inline-block">
              Creation Restricted
            </div>
          )}
          {(pending || info) && (
            <div className="text-xs text-green-700 mt-2">{info || t('dash.paywall.info')}</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={pending}
            onClick={handleSubscribe}
            className={`text-xs px-3 py-1.5 rounded ${pending ? 'bg-green-300 cursor-not-allowed text-white' : 'bg-green-600 text-white'}`}
          >
            {pending ? t('dash.paywall.pending') : t('dash.trial.cta')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
